import React, { useContext, useEffect } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { UserContext } from '../context/UserContext';
import { getCommunityLoginUrl } from '@atlassian/community-library';
import { getEnvironmentAsEnum } from '../utils/environment';
import { PageWithAnalytics } from '../components/common/analytics-wrapped/PageWithAnalytics';
import DocumentHead from '../components/common/DocumentHead';
import { DEFAULT_CHAMPIONS_SIGNUP_URL } from '../utils/constants';
import { championsAuthSignupIsActive } from '../services/statsig/featureGates';

const TITLE = 'Champions Signup | Atlassian Community';
const DESCRIPTION = 'Apply to become an Atlassian Champion.';
const CHAMPIONS_SIGNUP_PATH = '/champions/signup';

export const ChampionsSignupPage: React.FC = () => {
  const userData = useContext(UserContext);
  const user = userData?.user;
  const userIsInitialized = userData?.userIsInitialized;
  const isLoggedIn = !!user;
  const currentEnv = getEnvironmentAsEnum();
  const isAuthSignupEnabled = championsAuthSignupIsActive();

  useEffect(() => {
    // If feature flag is disabled, redirect to external signup page
    if (!isAuthSignupEnabled) {
      window.location.href = DEFAULT_CHAMPIONS_SIGNUP_URL;
      return;
    }

    // Only redirect after user state is initialized and confirmed not logged in
    if (userIsInitialized && !isLoggedIn) {
      // Redirect to login with this page as the post-login destination
      const loginUrl = getCommunityLoginUrl(
        {
          redirectUrl: `${process.env.HOME_BASE_URL}${CHAMPIONS_SIGNUP_PATH}`,
          experience: 'entry-auth',
        },
        currentEnv,
      );
      window.location.href = loginUrl;
    }
  }, [isAuthSignupEnabled, userIsInitialized, isLoggedIn, currentEnv]);

  // Show loading state while checking auth, redirecting, or feature flag is disabled
  if (!isAuthSignupEnabled || !userIsInitialized || !isLoggedIn) {
    return (
      <div css={pageStyles}>
        <div css={containerStyles}>
          <p css={descriptionStyles}>Loading...</p>
        </div>
      </div>
    );
  }

  // User is authenticated and feature flag is enabled - show the signup form placeholder
  return (
    <PageWithAnalytics eventScreenName="championsSignupScreen">
      <DocumentHead title={TITLE} description={DESCRIPTION} />
      <div css={pageStyles}>
        <div css={containerStyles}>
          <h1 css={headingStyles}>Champions Signup</h1>
          <p css={descriptionStyles}>This will be the champions signup form</p>
        </div>
      </div>
    </PageWithAnalytics>
  );
};

export default ChampionsSignupPage;

const containerStyles = css({
  maxWidth: '800px',
  minWidth: '375px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: token('space.600'),
});

const headingStyles = css({
  marginBottom: token('space.200'),
  fontFamily: token('font.family.brand.heading'),
  fontSize: '32px',
  fontWeight: token('font.weight.semibold'),
  lineHeight: '40px',
  textAlign: 'center',
});

const descriptionStyles = css({
  marginBottom: token('space.200'),
  color: token('color.text'),
  fontFamily: 'Inter',
  fontWeight: '400',
  lineHeight: '24px',
  textAlign: 'center',
  fontSize: '18px',
});

const pageStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: token('space.800'),
  paddingBottom: token('space.800'),
  minHeight: '400px',
});
